/**
 * Admin-Bewerbungen: vollständige Übersicht aller Karriere-Bewerbungen.
 * Zeigt auch Bewerbungen, deren Brevo-Versand fehlgeschlagen ist (z.B. weil
 * Production-Env-Vars zur Submission-Zeit fehlten) und erlaubt manuellen
 * Re-Versand. Nur für admin-Rolle sichtbar.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { fokusId } from '../lib/fokus';
import { BewerbungEinladen } from '../components/BewerbungEinladen';
import { BewerbungKennenlernen } from '../components/BewerbungKennenlernen';
import { BewerbungNotizen } from '../components/BewerbungNotizen';
import { BewerbungPhasen } from '../components/BewerbungPhasen';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import {
  ArrowLeft,
  AlertCircle,
  CheckCircle2,
  Download,
  Loader2,
  Mail,
  RefreshCw,
  Send,
  Trash2,
  Briefcase,
  FileText,
} from 'lucide-react';
import { api, useAuth, API } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';

const STATUSES = [
  { key: 'neu',            label: 'NEU',            cls: 'bg-[#b8893d]/15 text-[#b8893d]', done: false },
  { key: 'in_bearbeitung', label: 'IN BEARBEITUNG', cls: 'bg-blue-50 text-blue-700',        done: false },
  { key: 'kontaktiert',    label: 'KONTAKTIERT',    cls: 'bg-indigo-50 text-indigo-700',    done: false },
  { key: 'abgesagt',       label: 'ABGESAGT',       cls: 'bg-gray-100 text-gray-600',       done: true  },
  { key: 'eingestellt',    label: 'EINGESTELLT',    cls: 'bg-green-50 text-green-700',      done: true  },
];
const STATUS_PRIO = { neu: 0, in_bearbeitung: 1, kontaktiert: 2, abgesagt: 3, eingestellt: 4 };
const isBewerbungDone = (s) => s === 'abgesagt' || s === 'eingestellt';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const AdminBewerbungen = () => {
  const navigate = useNavigate();
  const { user: authUser, loading: authLoading, isAdmin } = useAuth();
  const { toast } = useToast();

  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, unsent: 0, mailer: { ready: true } });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(fokusId() ? 'all' : 'open'); // open | done | unsent | all
  const [resending, setResending] = useState(null);
  const [ansicht, setAnsicht] = useState('liste');
  const [deletingId, setDeletingId] = useState(null);

  const glow = (id) => setTimeout(() => {
      const el = document.querySelector(`[data-fokus="${id}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.remove('fokus-glow'); void el.offsetWidth; el.classList.add('fokus-glow');
      setTimeout(() => el.classList.remove('fokus-glow'), 3200);
    }, 120);
  const aktualisiert = (d, scroll = true) => {
    setItems((xs) => xs.map((x) => (x.bewerbung_id === d.bewerbung_id ? { ...x, ...d } : x)));
    if (scroll) glow(d.bewerbung_id);
  };
  const oeffnen = (id) => { setAnsicht('liste'); setFilter('all'); glow(id); };
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/anfragen/bewerbungen');
      setItems(data.items || []);
      setStats({
        total: data.total || 0,
        unsent: data.unsent || 0,
        mailer: data.mailer || { ready: true },
      });
    } catch (e) {
      toast({
        title: 'Laden fehlgeschlagen',
        description: e?.response?.data?.detail || e.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (authLoading) return;
    if (!authUser) {
      navigate('/login?next=/admin/bewerbungen');
      return;
    }
    if (!['admin', 'kanzlei_mitarbeiter'].includes(authUser.role)) {
      navigate('/');
      return;
    }
    load();
  }, [authUser, authLoading, navigate, load]);

  const openCount = useMemo(() => items.filter((it) => !isBewerbungDone(it.status)).length, [items]);
  const doneCount = items.length - openCount;

  const visible = useMemo(() => {
    let list = items;
    if (filter === 'open') list = list.filter((it) => !isBewerbungDone(it.status));
    else if (filter === 'done') list = list.filter((it) => isBewerbungDone(it.status));
    else if (filter === 'unsent') list = list.filter((it) => !it.email_sent);
    // Sortierung: offene zuerst, innerhalb gleicher Status-Prio neueste zuerst
    return [...list].sort((a, b) => {
      const pa = STATUS_PRIO[a.status] ?? 0;
      const pb = STATUS_PRIO[b.status] ?? 0;
      if (pa !== pb) return pa - pb;
      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    });
  }, [filter, items]);

  const downloadCv = async (it) => {
    try {
      const response = await api.get(
        `/admin/anfragen/bewerbungen/${it.bewerbung_id}/cv`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      const safe = (it.name || 'Bewerber').replace(/\s+/g, '_');
      link.href = url;
      link.setAttribute('download', `Lebenslauf_${safe}_${it.bewerbung_id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast({
        title: 'Download fehlgeschlagen',
        description: e?.response?.data?.detail || 'Lebenslauf konnte nicht geladen werden.',
        variant: 'destructive',
      });
    }
  };

  const downloadFullPdf = async (it) => {
    try {
      const response = await api.get(
        `/admin/anfragen/bewerbungen/${it.bewerbung_id}/pdf`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      const safe = (it.name || 'Bewerber').replace(/\s+/g, '_');
      link.href = url;
      link.setAttribute('download', `Bewerbung_${safe}_${it.bewerbung_id}.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: 'PDF erstellt', description: 'Die Bewerbung wurde als PDF heruntergeladen.' });
    } catch (e) {
      toast({
        title: 'PDF-Export fehlgeschlagen',
        description: e?.response?.data?.detail || 'Bitte erneut versuchen.',
        variant: 'destructive',
      });
    }
  };

  const resendOne = async (it) => {
    setResending(it.bewerbung_id);
    try {
      const { data } = await api.post(`/admin/anfragen/bewerbungen/${it.bewerbung_id}/resend`);
      toast({
        title: 'E-Mail erneut versendet',
        description: data.error
          ? `Mit Hinweis: ${data.error}`
          : `An ${stats.mailer?.mail_to || 'Kanzlei'} und ${it.email}`,
      });
      await load();
    } catch (e) {
      toast({
        title: 'Versand fehlgeschlagen',
        description: e?.response?.data?.detail || e.message,
        variant: 'destructive',      });
    } finally {
      setResending(null);
    }
  };

  const resendAllUnsent = async () => {
    const unsent = items.filter((it) => !it.email_sent);
    if (unsent.length === 0) return;
    if (!window.confirm(`${unsent.length} Bewerbung(en) erneut per Brevo versenden?`)) return;
    let ok = 0;
    let failed = 0;
    for (const it of unsent) {
      setResending(it.bewerbung_id);
      try {
        await api.post(`/admin/anfragen/bewerbungen/${it.bewerbung_id}/resend`);
        ok += 1;
      } catch {
        failed += 1;
      }
    }
    setResending(null);
    toast({
      title: 'Bulk-Versand abgeschlossen',
      description: `${ok} erfolgreich, ${failed} fehlgeschlagen.`,
    });
    await load();
  };

  const updateStatus = async (it, status) => {
    try {
      await api.patch(`/admin/anfragen/bewerbungen/${it.bewerbung_id}`, { status });
      await load();
    } catch (e) {
      toast({
        title: 'Status-Update fehlgeschlagen',
        description: e?.response?.data?.detail || e.message,
        variant: 'destructive',
      });
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Bewerbung von ${it.name} (${it.email}) wirklich löschen?`)) return;
    setDeletingId(it.bewerbung_id);
    try {
      await api.delete(`/admin/anfragen/bewerbungen/${it.bewerbung_id}`);
      await load();
    } catch (e) {
      toast({
        title: 'Löschen fehlgeschlagen',
        description: e?.response?.data?.detail || e.message,
        variant: 'destructive',
      });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f8fa]">
      <Header />
      <main className="pt-[140px] pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
            <div>
              <div className="flex items-center gap-3 text-[#b8893d] text-xs tracking-[0.3em] mb-2">
                <Briefcase className="w-3.5 h-3.5" /> ADMIN · KARRIERE
              </div>
              <h1 className="font-serif text-3xl md:text-4xl text-[#1B3A5C]">Bewerbungen</h1>
              <p className="text-[#34536b] text-sm mt-1">
                Übersicht aller eingegangenen Karriere-Bewerbungen mit Lebenslauf-Download und Re-Versand.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                onClick={load}
                variant="outline"
                className="border-[#1B3A5C]/20 text-[#1B3A5C]"
                data-testid="bewerbungen-refresh"
              >
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Neu laden
              </Button>
              {stats.unsent > 0 && (
                <Button
                  type="button"
                  onClick={resendAllUnsent}
                  className="bg-[#b8893d] hover:bg-[#a07730] text-white"
                  data-testid="bewerbungen-resend-all"
                >
                  <Send className="w-3.5 h-3.5 mr-1.5" />
                  Alle {stats.unsent} ungesendeten erneut versenden
                </Button>
              )}
            </div>
          </div>

          {/* Mailer (Brevo) status banner */}
          {!stats.mailer?.ready && (
            <div
              className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-sm flex items-start gap-3"
              data-testid="mailer-status-banner"
            >
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold text-red-700 mb-1">
                  Brevo ist auf dieser Umgebung NICHT vollständig konfiguriert.
                </p>
                <ul className="text-red-700 text-xs space-y-0.5">
                  <li>
                    BREVO_API_KEY:{' '}
                    {stats.mailer?.brevo_api_key ? (
                      <span className="text-green-700">gesetzt ✓</span>
                    ) : (
                      <span className="font-bold">FEHLT ✗</span>
                    )}
                  </li>
                  <li>
                    MAIL_TO:{' '}
                    {stats.mailer?.mail_to || <span className="font-bold">FEHLT ✗</span>}
                  </li>
                  <li>
                    MAIL_FROM_EMAIL: {stats.mailer?.mail_from_email || <span className="font-bold">FEHLT ✗</span>}
                  </li>
                  <li>
                    MAIL_FROM_NAME: {stats.mailer?.mail_from_name || <span className="font-bold">FEHLT ✗</span>}
                  </li>
                </ul>
                <p className="text-red-700 text-xs mt-2">
                  Bewerbungen werden weiterhin in der DB gespeichert. Setzen Sie die Variablen im
                  Emergent Deployment-Dashboard und nutzen Sie anschließend „Alle ungesendeten erneut
                  versenden&ldquo;.
                </p>
              </div>
            </div>
          )}

          {stats.mailer?.ready && stats.unsent > 0 && (
            <div
              className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-6 rounded-sm flex items-start gap-3"
              data-testid="unsent-banner"
            >
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">
                <strong>{stats.unsent}</strong> Bewerbung(en) wurden nicht erfolgreich per E-Mail
                verschickt. Brevo ist jetzt konfiguriert – klicken Sie oben auf „Alle ungesendeten
                erneut versenden&ldquo;.
              </p>
            </div>
          )}

          {/* Stats + Filter */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex flex-wrap gap-6 text-sm">
              <span className="text-[#34536b]">
                Gesamt: <strong className="text-[#1B3A5C]">{stats.total}</strong>
              </span>
              <span className="text-[#34536b]">
                Offen: <strong className="text-amber-700">{openCount}</strong>
              </span>
              <span className="text-[#34536b]">
                Erledigt: <strong className="text-green-700">{doneCount}</strong>
              </span>
              <span className="text-[#34536b]">
                Ungesendet:{' '}
                <strong className={stats.unsent > 0 ? 'text-amber-600' : 'text-green-700'}>
                  {stats.unsent}
                </strong>
              </span>
            </div>
            <div className="inline-flex border border-gray-200 rounded-sm overflow-hidden" data-testid="bewerbungen-ansicht">
              {[['liste', 'LISTE'], ['phasen', 'PHASEN']].map(([k, l]) => (
                <button key={k} type="button" onClick={() => setAnsicht(k)} className={`px-3 py-1.5 text-[11px] tracking-[0.2em] font-medium transition-colors ${ansicht === k ? 'bg-[#b8893d] text-white' : 'bg-white text-[#34536b] hover:bg-[#f5f8fa]'}`} data-testid={`ansicht-${k}`}>{l}</button>
              ))}
            </div>
            {ansicht === 'liste' && <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filter">
              {[
                { key: 'open',   label: `OFFEN (${openCount})` },
                { key: 'done',   label: `ERLEDIGT (${doneCount})` },
                { key: 'unsent', label: `NUR UNGESENDETE (${stats.unsent})` },
                { key: 'all',    label: `ALLE (${items.length})` },
              ].map((f) => {
                const active = filter === f.key;
                return (
                  <button
                    key={f.key}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setFilter(f.key)}
                    data-testid={`filter-${f.key}`}
                    className={`px-3 py-1.5 text-[11px] tracking-[0.2em] font-medium rounded-sm border transition-colors ${
                      active
                        ? 'bg-[#1B3A5C] text-white border-[#1B3A5C]'
                        : 'bg-white text-[#34536b] border-gray-200 hover:border-[#1B3A5C]'
                    }`}
                  >
                    {f.label}
                  </button>
                );
              })}
            </div>}
          </div>

          {/* List */}
          {!loading && ansicht === 'phasen' ? (
            <BewerbungPhasen items={items} toast={toast} onDone={aktualisiert} onOpen={oeffnen} />
          ) : loading ? (
            <div className="text-center py-20">
              <Loader2 className="w-8 h-8 text-[#b8893d] animate-spin mx-auto" />
            </div>
          ) : visible.length === 0 ? (
            <div className="bg-white p-12 text-center rounded-sm border-2 border-dashed border-gray-200">
              <Briefcase className="w-10 h-10 text-[#b8893d]/40 mx-auto mb-3" />
              <p className="text-[#34536b] text-sm">
                {filter === 'unsent'
                  ? 'Keine ungesendeten Bewerbungen – alles versendet ✓'
                  : filter === 'open'
                  ? 'Keine offenen Bewerbungen – alle erledigt ✓'
                  : filter === 'done'
                  ? 'Bisher keine Bewerbungen erledigt.'
                  : 'Bisher keine Bewerbungen eingegangen.'}
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-sm shadow-sm overflow-hidden" data-testid="bewerbungen-list">
              {visible.map((it, idx) => {
                const isResending = resending === it.bewerbung_id;
                const isDeleting = deletingId === it.bewerbung_id;
                return (
                  <article
                    key={it.bewerbung_id}
                    className={`p-5 ${idx > 0 ? 'border-t border-gray-100' : ''}`}
                    data-testid={`bewerbung-row-${it.bewerbung_id}`}
                    data-fokus={it.bewerbung_id}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-4 mb-3">
                      <div className="flex-1 min-w-[260px]">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <h3 className="text-[#1B3A5C] font-semibold text-base">{it.name}</h3>
                          {it.email_sent ? (
                            <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.15em] bg-green-50 text-green-700 px-2 py-0.5 rounded-sm">
                              <CheckCircle2 className="w-3 h-3" /> VERSENDET
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.15em] bg-amber-50 text-amber-700 px-2 py-0.5 rounded-sm">
                              <AlertCircle className="w-3 h-3" /> NICHT VERSENDET
                            </span>
                          )}
                          {(() => {
                            const cur = STATUSES.find((s) => s.key === (it.status || 'neu'));
                            return cur ? (
                              <span className={`text-[10px] tracking-[0.15em] px-2 py-0.5 rounded-sm ${cur.cls}`}>
                                {cur.label}
                              </span>
                            ) : null;
                          })()}
                        </div>
                        <div className="text-[#34536b] text-xs flex flex-wrap gap-x-4 gap-y-1">
                          <span>
                            <Mail className="inline w-3 h-3 mr-1" />
                            <a
                              href={`mailto:${it.email}`}
                              className="text-[#b8893d] hover:underline"
                              data-testid={`bewerbung-email-${it.bewerbung_id}`}
                            >
                              {it.email}
                            </a>
                          </span>
                          {it.phone && <span>📞 {it.phone}</span>}
                          <span>📋 {it.position || 'Initiativbewerbung'}</span>
                          <span className="text-gray-500">{fmtDate(it.created_at)}</span>
                          <span className="text-gray-400 font-mono text-[10px]">{it.bewerbung_id}</span>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-2">
                        <Button
                          type="button"
                          onClick={() => downloadFullPdf(it)}
                          variant="outline"
                          className="border-[#1B3A5C] bg-[#1B3A5C] hover:bg-[#34536b] text-white text-[11px]"
                          data-testid={`bewerbung-pdf-${it.bewerbung_id}`}
                        >
                          <FileText className="w-3.5 h-3.5 mr-1.5" /> ALS PDF EXPORTIEREN
                        </Button>
                        {it.cv?.gridfs_id && (
                          <Button
                            type="button"
                            onClick={() => downloadCv(it)}
                            variant="outline"
                            className="border-[#1B3A5C]/20 text-[#1B3A5C] text-[11px]"
                            data-testid={`bewerbung-cv-${it.bewerbung_id}`}
                          >
                            <Download className="w-3.5 h-3.5 mr-1.5" /> LEBENSLAUF
                            <span className="ml-1.5 text-[10px] text-gray-500">
                              ({Math.round((it.cv.size || 0) / 1024)} KB)
                            </span>
                          </Button>
                        )}
                        <Button
                          type="button"
                          onClick={() => resendOne(it)}
                          disabled={isResending}
                          className="bg-[#b8893d] hover:bg-[#a07730] text-white text-[11px]"
                          data-testid={`bewerbung-resend-${it.bewerbung_id}`}
                        >
                          {isResending ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <>
                              <Send className="w-3.5 h-3.5 mr-1.5" />
                              {it.email_sent ? 'ERNEUT VERSENDEN' : 'JETZT VERSENDEN'}
                            </>
                          )}
                        </Button>
                        {isAdmin && <Button
                          type="button"
                          onClick={() => remove(it)}
                          disabled={isDeleting}
                          variant="outline"
                          className="border-red-200 text-red-600 hover:bg-red-50 text-[11px]"
                          data-testid={`bewerbung-delete-${it.bewerbung_id}`}
                        >
                          {isDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                        </Button>}
                      </div>
                    </div>

                    {/* Message */}
                    {it.message && (
                      <div className="mt-2 mb-3 bg-[#fbf7f0] border-l-3 border-[#b8893d] p-3 rounded-sm text-sm text-[#34536b] whitespace-pre-wrap">
                        {it.message}
                      </div>
                    )}

                    {/* Status select + last error */}
                    <div className="flex flex-wrap items-center gap-3 mt-3 pt-3 border-t border-gray-100">
                      <label className="text-xs text-[#34536b] tracking-wider">STATUS:</label>
                      <select
                        value={it.status || 'neu'}
                        onChange={(e) => updateStatus(it, e.target.value)}
                        className="text-xs border border-gray-200 rounded-sm px-2 py-1 text-[#1B3A5C] bg-white"
                        data-testid={`bewerbung-status-${it.bewerbung_id}`}
                      >
                        {STATUSES.map((s) => (
                          <option key={s.key} value={s.key}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                      {it.last_error && (
                        <span className="text-xs text-red-600">
                          <AlertCircle className="inline w-3 h-3 mr-1" />
                          Letzter Fehler: {it.last_error}
                        </span>
                      )}
                      {it.last_resend_at && (
                        <span className="text-xs text-gray-500">
                          Zuletzt versendet: {fmtDate(it.last_resend_at)}
                        </span>
                      )}
                      <div className="w-full sm:w-auto sm:ml-auto flex flex-wrap items-center gap-2 justify-end">
                        {!it.absage && <BewerbungEinladen it={it} toast={toast} onDone={aktualisiert} />}
                        <BewerbungKennenlernen it={it} toast={toast} onDone={aktualisiert} />
                      </div>
                    </div>
                    <BewerbungNotizen it={it} toast={toast} onDone={(d) => aktualisiert(d, false)} />
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </main>
      <Footer />
      <Toaster />
    </div>
  );
};

export default AdminBewerbungen;
